/** Per-browser player options, saved in localStorage (UI preferences only; the server never sees them). */
import { useState } from 'react';
import { BOT_LEVELS, type BotLevel, type PlayStyle } from '@kadice/shared';
import { configureAudio } from './audio';
import { setFastAnimations } from './speed';
import { setMotionDice, setMotionMode } from './rolling';
import { canShow3d } from './dice3d/support';

export type { PlayStyle };
/**
 * 'portrait': the player of a turn big on the left, everyone else in a column on the right, with
 * the turn controls built for a thumb — one big button for the move of the moment, the rest in a
 * row under it, and taller dice arrows. (This was Portrait Plus; the old Portrait is retired and a
 * saved 'portraitplus' reads as 'portrait'.)
 * 'v1': the original layout (seats along the top, the big seat under them).
 */
export type TableLayout = 'portrait' | 'v1';
export const TABLE_LAYOUTS: readonly TableLayout[] = ['portrait', 'v1'];
/**
 * 'classic': the image dice and roll animation. 'motion': experimental 3D dice in a wooden tray,
 * thrown by holding the screen, shaking and flicking the phone, or swiping (see `dice3d/`,
 * `docs/design/motion-dice-plan.md`). 'popple': the same 3D dice, but you tap the bubble over them
 * and the game flings them. Classic where WebGL is missing or motion is reduced.
 */
export type DiceType = 'classic' | 'motion' | 'popple';
export const DICE_TYPES: readonly DiceType[] = ['classic', 'motion', 'popple'];

export interface Options {
  /** Glow the cards your kept dice (or free kills) can destroy. Off makes the game harder. */
  highlightAttackable: boolean;
  /** 'choose': you pick your card. 'random': a card is picked for you whenever one is needed. */
  playStyle: PlayStyle;
  /**
   * Beginner mode: the how-to-play hint line (and your card's power) on your turn, and a
   * full-screen 'your targets' intro each time play starts after cards are revealed.
   */
  beginnerMode: boolean;
  /** Testing: a 5x button on your turn that grants the five-of-a-kind power for that roll. */
  debug: boolean;
  /** Debug sub-option: show Smart bots' reasoning for each move as a notice. */
  debugBotReasons: boolean;
  /** Debug sub-option: show the kill-odds readout beside face-up cards. */
  debugStats: boolean;
  /** Level given to bots you add as host (each bot keeps the level it was added with). */
  botLevel: BotLevel;
  /** Sound effect volume, 0 to 1. */
  volume: number;
  /** Silences everything, sound effects and music. */
  muted: boolean;
  /** Lobby music (main lobby and a game's lobby). */
  music: boolean;
  /** Music volume, 0 to 1. */
  musicVolume: number;
  /** Game animations about 3x faster on this screen (see `speed.ts`). */
  fastAnimations: boolean;
  /** Round Over / Round N banners close on their own after `AUTO_NEXT_MS` (no Next button). */
  autoNext: boolean;
  /** Vibrate on game events where the browser can (see `haptics.ts`). */
  haptics: boolean;
  /** Fill the screen, no browser bars, where the browser can (see `fullscreen.ts`). */
  fullscreen: boolean;
  /** How the table is laid out during play (see `TableLayout`; wars and the lobby are the same in both). */
  layout: TableLayout;
  /** How dice are rolled and shown on this screen (see `DiceType`). */
  diceType: DiceType;
  /** 3D dice only: other players' and bots' rolls play in 3D too. Off: classic dice for them (faster). */
  enemy3d: boolean;
}

const DEFAULTS: Options = {
  highlightAttackable: true,
  playStyle: 'random',
  beginnerMode: true,
  debug: false,
  debugBotReasons: false,
  debugStats: true,
  botLevel: 'default',
  volume: 0.7,
  muted: false,
  music: true,
  musicVolume: 0.6,
  fastAnimations: false,
  autoNext: false,
  haptics: true,
  fullscreen: false,
  layout: 'portrait',
  diceType: 'classic',
  enemy3d: true,
};
const KEY = 'kadice.options';

function load(): Options {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Partial<Options>;
    const options = { ...DEFAULTS, ...saved };
    if (!BOT_LEVELS.includes(options.botLevel)) options.botLevel = DEFAULTS.botLevel;
    if (!TABLE_LAYOUTS.includes(options.layout)) options.layout = DEFAULTS.layout;
    if (!DICE_TYPES.includes(options.diceType)) options.diceType = DEFAULTS.diceType;
    if (typeof options.volume !== 'number' || !(options.volume >= 0 && options.volume <= 1))
      options.volume = DEFAULTS.volume;
    if (typeof options.musicVolume !== 'number' || !(options.musicVolume >= 0 && options.musicVolume <= 1))
      options.musicVolume = DEFAULTS.musicVolume;
    return options;
  } catch {
    return DEFAULTS;
  }
}

/** Put the dice options in force (3D or classic, Popple, enemies' rolls in 3D). */
export function applyDice(options: Pick<Options, 'diceType' | 'enemy3d'>): void {
  setMotionDice(options.diceType !== 'classic' && canShow3d());
  setMotionMode(options.diceType === 'popple', options.enemy3d !== false);
}

export function useOptions(): [Options, (patch: Partial<Options>) => void] {
  const [options, setOptions] = useState(load);
  configureAudio(options);
  setFastAnimations(options.fastAnimations);
  applyDice(options);
  const update = (patch: Partial<Options>) => {
    const next = { ...options, ...patch };
    setOptions(next);
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      // Storage blocked: the option still applies for this session.
    }
  };
  return [options, update];
}
